import SAPOMailConfig      from "@/SAPOMailConfig";
import EventBroadcaster from "@/js/Common/Events/EventBroadcaster";
import Events from "@/js/Common/Events/Events";
import LocalStorageService from "@/js/Shared/Services/Storage/LocalStorageService";

export { BrowserNotificationsController as default };

/**
* Handles All Browser Notifications
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class BrowserNotificationsControllerSingleton {
  /**
   * Defines if browser notifications are blocked
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Boolean}
   */
  blocked = false;

  /**
   * Defines if notifications are enabled
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {number}
   */
  enabled = 0;

  supported;

  /**
   * Browser native notification api
   *
   * @public
   *
   * @since 7.0.0
   *
   * @type {Notification}
   */
  api;

  /**
   * Constructor
   *
   * Loads browser notification settings from local storage
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  constructor() {
    this.setSupported();

    if (this.supported === false) {
      return;
    }

    this.setAPI();
    this.loadStorage();
    this.setSettings();
  }

  /**
   * Request permission to the browser api and enables/disables
   * notifications accordingly
   *
   * @public
   *
   * @since 7.0.0
   *
   * @return {Promise<void>}
   */
  async requestPermission() {
    await this.api.requestPermission();
    this.setSettings();

    if (this.api.permission === 'granted') {
      this.setEnabled(1);
    }
  }

  /**
   * Changes the settings accordingly to the browser permission
   * setting
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  setSettings() {
    if (this.api.permission === 'default') {
      this.setEnabled(0);
      this.setBlocked(false);
      return;
    }

    if (this.api.permission === 'denied') {
      this.setEnabled(0);
      this.setBlocked(true);
      return;
    }

    this.setBlocked(false);
  }

  /**
   * Sets the enabled value
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {Boolean} value
   *
   * @return {void}
   */
  setEnabled(value) {
    this.enabled = value;
    this.saveStorage();
  }

  /**
   * Sets the blocked value
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Boolean} value
   *
   * @return {void}
   */
  setBlocked(value) {
    this.blocked = value;
  }

  setSupported() {
    this.supported = ('Notification' in window) ? true : false;
  }

  setAPI () {
    this.api = Notification;
  }

  /**
   * Loads local storage settings
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  loadStorage() {
    this.setEnabled(
      parseInt(LocalStorageService.getItem('s_son', 0))
    );
  }

  /**
   * Sends a browser desktop notification
   *
   * For more information about the params, check the JS Notification API Documentation
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {String} title notification title
   * @param {String} text  notification text
   * @param {Object} data  notification data
   *
   * @return {void}
   */
  notify(title, text, data) {
    let path   = SAPOMailConfig.server.environment.static;

    let notification = new Notification(
      title, {
        body: text,
        tag: 'incoming-message',
        data: data,
        icon: path.cdn  +  path.images.logos.path + path.images.logos['touch-192'],
    });

    notification.addEventListener('click', (event) => {
      notification.close();
      EventBroadcaster.broadcast(Events.PUSH_NOTIFICATIONS_CLICK, data);
      window.parent.parent.focus();
    })
  }

  /**
   * Saves notifications settings in the local storage
   *
   * @private
   *
   * @since 7.0.0
   *
   * @return {void}
   */
  saveStorage() {
    LocalStorageService.setItem('s_son', this.enabled);
  }
}

const BrowserNotificationsController = new BrowserNotificationsControllerSingleton();